import React, { useState, useEffect } from "react";
import { Briefcase } from "lucide-react";
import ReusableDropdown from "../../../components/ReusableDropdown";
import employeeActionsAPI from "../utils/changesAPIs";

const ManagerAutocomplete = ({ value, onChange, theme, language, t, managers = [] }) => {
  const [fetchedManagers, setFetchedManagers] = useState([]);

  // Fallback: if no managers were provided by the parent, fetch the whole
  // company roster directly. Any employee of the same company may be chosen
  // as a manager, so this is the client-wide (not visibility-scoped) list.
  useEffect(() => {
    if (!Array.isArray(managers) || managers.length === 0) {
      const clientName = localStorage.getItem("clientName");
      if (clientName) {
        employeeActionsAPI
          .listManagerOptions(clientName)
          .then((list) => {
            if (Array.isArray(list) && list.length > 0) {
              setFetchedManagers(
                list.map((emp) => ({
                  infinityCode: emp.infinityCode,
                  clientCode: emp.clientCode || emp.infinityCode,
                  arabicName: emp.arabicName || null,
                  sr: emp.sr || emp.name || null,
                  departmentNames: emp.departmentNames || (emp.departmentName ? [emp.departmentName] : (emp.employeeType ? [emp.employeeType] : [])),
                }))
              );
            }
          })
          .catch(() => {});
      }
    }
  }, [managers]);

  const getEmployeeName = (manager) => {
    return language === "ar"
      ? manager.arabicName || manager.sr || manager.name || t.notAvailable
      : manager.sr || manager.arabicName || manager.name || t.notAvailable;
  };

  const getEmployeeLabel = (manager) => {
    const code = manager.clientCode || manager.infinityCode || "";
    const name = getEmployeeName(manager);
    return code ? `${code} — ${name}` : name;
  };

  const availableManagers = Array.isArray(managers) && managers.length > 0 ? managers : fetchedManagers;

  // Match on English name, Arabic name, client code or infinity code.
  const matchesManager = (manager, searchTerm) => {
    const q = String(searchTerm || "").toLowerCase();
    return [manager.sr, manager.name, manager.arabicName, manager.clientCode, manager.infinityCode]
      .some((field) => String(field || "").toLowerCase().includes(q));
  };

  const handleSelect = (manager) => {
    onChange({
      target: {
        name: "reportsTo",
        value: getEmployeeName(manager),
      },
    });
  };

  const handleRemove = () => {
    onChange({
      target: {
        name: "reportsTo",
        value: "",
      },
    });
  };

  return (
    <ReusableDropdown
      items={availableManagers}
      value={value || ""}
      onSelect={handleSelect}
      onClear={handleRemove}
      placeholder={t.searchForManagers}
      emptyMessage={t.noManagersFound}
      getItemKey={(manager) => manager.infinityCode || manager.clientCode}
      getItemLabel={getEmployeeLabel}
      filterFunction={matchesManager}
      renderItem={(manager) => (
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {getEmployeeLabel(manager)}
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "4px",
              fontSize: "12px",
              marginTop: "2px",
              color: theme === "dark" ? "#94a3b8" : "#64748b",
              fontWeight: 500,
            }}
          >
            <Briefcase size={12} />
            {Array.isArray(manager.departmentNames) && manager.departmentNames.length > 0
              ? manager.departmentNames.join(", ")
              : t.notAvailable}
          </div>
        </div>
      )}
      theme={theme}
      language={language}
    />
  );
};

export default ManagerAutocomplete;